import React, { useState, useEffect, useContext, useRef } from 'react';
import './SurveyModal.css';
import Modal from '../Modal/Modal';
import Timer from '../Timer/Timer';
import ActionButtons from '../ActionButtons/ActionButtons';
import baseApi from '../../common/api/base';
import { EnvironmentContext } from '../../contexts/EnvironmentContext';
import logComponent from '../../common/logComponent';
import IntegerInput from '../IntegerInput/IntegerInput';
import DecimalInput from '../DecimalInput/DecimalInput';
import DateTimeInput from '../DateTimeInput/DateTimeInput';
import NumericSlider from '../NumericSlider/NumericSlider';
import GraphicalSlider from '../GraphicalSlider/GraphicalSlider';
import SearchSelect from '../SearchSelect/SearchSelect';
import TextAreaInput from '../TextAreaInput/TextAreaInput';
import RadioButtonListInput from '../RadioButtonListInput/RadioButtonListInput';
import TextInput from '../TextInput/TextInput';
import ToggleInput from '../ToggleInput/ToggleInput';
import lowerCaseFirstLetter from '../../common/lowerCaseFirstLetter';
import MonitoredIndividualImage from '../MonitoredIndividualImage/MonitoredIndividualImage';

export default function SurveyModal({ close, survey, title }) {
    const environmentContext = useContext(EnvironmentContext);
    const [status, setStatus] = useState('initial');
    const [modalShouldExit, setModalShouldExit] = useState(false);
    const [startTime, setStartTime] = useState(Date.now());
    const [formIsInvalid, setFormIsInvalid] = useState(false);
    const [validating, setValidating] = useState(false);
    const { hasToken, activeUser: user, organization, selectedMonitoredIndividual, selectedUnit } = environmentContext;
    const facilityId = selectedMonitoredIndividual?.lastKnownFacilityId ?? selectedUnit.facilityId ?? user.facilityId;
    const formIsInvalidRef = useRef(false);
    const canCorrespond = user.hasCorrespondenceViaGroupsOrOverride;

    const setFormIsInvalidRef = (el) => {
        if (logComponent('SurveyModal')) { console.debug('SurveyModal setFormIsInvalidRef'); }
        formIsInvalidRef.current = el;
        setFormIsInvalid(el);
    }

    const surveyModalRef = useRef({
        startOn: new Date(),
        endOn: null,
        surveyProgress: null,
        isComplete: false,
        monitoredIndividualId: selectedMonitoredIndividual?.monitoredIndividualId,
        unitId: selectedUnit?.unitId,
        surveyQuestionResponses: [],
        userId: user.userId,
        surveyResponseId: null,
        externalId: null,
        localStartOn: new Date(),
        localEndOn: null,
        facilityId: facilityId,
        surveyId: survey.surveyId,
        surveyDistributionId: null,
        surveyContactId: null,
        surveyResponseScoreSections: null,
        surveyScoreId: survey.surveyScoreId,
        score: 0,
        sourceId: 4


    });
    const [surveyQuestions, setSurveyQuestions] = useState(false);
    const surveyQuestionsRef = useRef(null);

    const setSurveyModalRef = (el) => {
        if (logComponent('SurveyModal')) { console.debug('SurveyModal setSurveyModalRef'); }
        surveyModalRef.current = el;
    }

    const setSurveyQuestionsRef = (el) => {
        if (logComponent('SurveyModal')) { console.debug('SurveyModal setSurveyQuestionsRef'); }
        surveyQuestionsRef.current = el;
    }

    const actionButtons = useRef(null);
    const modalRef = useRef(null);
    const setModalRef = (el) => {
        if (logComponent('SurveyModal')) { console.debug('SurveyModal setModalRef'); }
        modalRef.current = el;
    }


    if (logComponent('SurveyModal')) { console.debug('SurveyModal rendered'); }


    useEffect(() => {
        if (logComponent('SurveyModal')) { console.debug('SurveyModal onMount'); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
        if (!hasToken) return;
        getSurveyQuestions();
        initializeSurveyQuestionResponses();

    }, []);

    const getQuestionTypesSettings = () => {
        return [
            { typeName: 'Undefined/No Type', responseField: 'stringValue', responseAdditionalField: null, hasItems: false, hasGraphics: false, hasText: true, hasNumbers: false, mustSelectOne: false, errorMessage: { required: 'Please provide an answer.  500 characters or fewer' }, errorMessageAdditional: null, hasDate: false, hasTime: false },
            { typeName: 'Multiple Choice', responseField: 'checkOptions', responseAdditionalField: null, hasItems: true, hasGraphics: false, hasText: false, hasNumbers: false, mustSelectOne: false, errorMessage: 'Please select one of the options.', errorMessageAdditional: null, hasDate: false, hasTime: false },
            { typeName: 'Numeric (integer)', responseField: 'intValue', responseAdditionalField: null, hasItems: false, hasGraphics: false, hasText: false, hasNumbers: false, mustSelectOne: false, errorMessage: 'Please provide a valid whole number.', errorMessageAdditional: null, hasDate: false, hasTime: false },
            { typeName: 'Numeric (decimal)', responseField: 'decimalValue', responseAdditionalField: null, hasItems: false, hasGraphics: false, hasText: false, hasNumbers: false, mustSelectOne: false, errorMessage: 'Please provide a valid number.', errorMessageAdditional: null, hasDate: false, hasTime: false },
            { typeName: 'Time', responseField: 'dateValue', responseAdditionalField: null, hasItems: false, hasGraphics: false, hasText: false, hasNumbers: false, mustSelectOne: false, errorMessage: 'Please provide a valid time.', errorMessageAdditional: null, hasDate: false, hasTime: true },
            { typeName: 'Time/Date', responseField: 'dateValue', responseAdditionalField: null, hasItems: false, hasGraphics: false, hasText: false, hasNumbers: false, mustSelectOne: false, errorMessage: 'Please provide a valid date and time.', errorMessageAdditional: null, hasDate: true, hasTime: true },
            { typeName: 'Date', responseField: 'dateValue', responseAdditionalField: null, hasItems: false, hasGraphics: false, hasText: false, hasNumbers: false, mustSelectOne: false, errorMessage: 'Please provide a valid date.', errorMessageAdditional: null, hasDate: true, hasTime: false },
            { typeName: 'Large Text Entry', responseField: 'stringValue', responseAdditionalField: null, hasItems: false, hasGraphics: false, hasText: true, hasNumbers: false, mustSelectOne: false, errorMessage: { required: 'Please provide an answer.  500 characters or fewer' }, errorMessageAdditional: null, hasDate: false, hasTime: false },
            { typeName: 'Small Text Entry', responseField: 'stringValue', responseAdditionalField: null, hasItems: false, hasGraphics: false, hasText: true, hasNumbers: false, mustSelectOne: false, errorMessage: { required: 'Please provide an answer.  500 characters or fewer' }, errorMessageAdditional: null, hasDate: false, hasTime: false },
            { typeName: 'Toggle', responseField: 'booleanValue', responseAdditionalField: null, hasItems: false, hasGraphics: false, hasText: false, hasNumbers: true, mustSelectOne: false, errorMessage: 'Please select yes or no.', errorMessageAdditional: null, hasDate: false, hasTime: false },
            { typeName: 'Slider', responseField: 'intValue', responseAdditionalField: null, hasItems: false, hasGraphics: false, hasText: false, hasNumbers: true, mustSelectOne: false, errorMessage: 'Please select one of the options.', errorMessageAdditional: null, hasDate: false, hasTime: false },
            { typeName: 'Graphic Slider', responseField: 'intValue', responseAdditionalField: null, false: true, hasGraphics: true, hasText: false, hasNumbers: true, mustSelectOne: false, errorMessage: 'Please select one of the options.', errorMessageAdditional: null, hasDate: false, hasTime: false },
            { typeName: 'Multiple Choice List with Search', responseField: 'checkOptions', responseAdditionalField: null, hasItems: true, hasGraphics: false, hasText: false, hasNumbers: false, mustSelectOne: true, errorMessage: 'Please select one of the options.', errorMessageAdditional: null, hasDate: false, hasTime: false },
            { typeName: 'Multiple Choice with Text Entry', responseField: 'checkOptions', responseAdditionalField: 'additionalStringValue', hasItems: true, hasGraphics: false, hasText: false, hasNumbers: false, mustSelectOne: true, errorMessage: 'Please select one of the options.', errorMessageAdditional: { required: 'Please provide additional text.  500 characters or fewer' }, hasDate: false, hasTime: false }
        ];
    }
    const getSurveyQuestions = () => {
        if (logComponent('SurveyModal')) { console.debug('SurveyModal getSurveyQuestions'); }
        survey.surveyQuestions.sort((a, b) => (a.questionSequence > b.questionSequence) ? 1 : -1);
        let questionTypes = getQuestionTypesSettings();

        let newSurveyQuestions = survey.surveyQuestions.map(surveyQuestion => {
            if (surveyQuestion.surveyQuestionOptions) {
                surveyQuestion.surveyQuestionOptions.sort((a, b) => (a.optionSequence > b.optionSequence) ? 1 : -1);
            }

            let questionTypesWithText = questionTypes.filter(questionType => questionType.hasText === true);
            let questionTypesWithMustSelectOne = questionTypes.filter(questionType => questionType.mustSelectOne === true);

            let surveyQuestionId = surveyQuestion.surveyQuestionId;
            let label = surveyQuestion.questionText;
            let labelAdditional = surveyQuestion.questionAdditionalText;
            let questionSequence = surveyQuestion.questionSequence;

            let questionType = questionTypes.find(questionType => questionType.typeName === surveyQuestion.surveyQuestionType.typeName);
            let responseField = questionType.responseField;
            let responseAdditionalField = questionType.responseAdditionalField;

            let name = 'question_' + surveyQuestion.questionSequence;
            let nameAdditional = surveyQuestion.questionSequence + '_' + (questionType.responseAdditionalField ?? 'additionalField');
            let rules = surveyQuestion.required ?
                { required: true } :
                {};
            if (questionTypesWithText.find(item => item.typeName === surveyQuestion.surveyQuestionType.typeName) !== undefined) {
                rules.maxLength = 500;
            }
            if (questionTypesWithMustSelectOne.find(item => item.typeName === surveyQuestion.surveyQuestionType.typeName) !== undefined) {
                rules.minItems = 1;
            }
            let rulesAdditional = { maxLength: 500 };
            let readOnly = false;
            let tabIndex = surveyQuestion.questionSequence;
            let initialValue = null;
            let instructions = surveyQuestion.questionInstructions;
            let errorMessage = questionType.errorMessage;
            let errorMessageAdditional = questionType.errorMessageAdditional;
            let errorMessages = [errorMessage];
            let requiresCorrespondence = surveyQuestion.isCheckInSurveyFamilyNoteQuestion;


            let surveyQuestionOptions = null;

            if (surveyQuestion.surveyQuestionOptions) {
                surveyQuestionOptions = surveyQuestion.surveyQuestionOptions.map(surveyQuestionOption => {
                    let key = surveyQuestionOption.optionText;
                    let value = surveyQuestionOption.optionValueString;
                    let iconUrl = surveyQuestionOption.optionUri;
                    let iconSvg = surveyQuestionOption.optionSvg;
                    let type = "string";
                    if (surveyQuestionOption.optionValueInt) {
                        value = surveyQuestionOption.optionValueInt;
                        type = "int";
                    }
                    if (surveyQuestionOption.optionValueLong) {
                        value = surveyQuestionOption.optionValueLong;
                        type = "long";
                    }
                    if (surveyQuestionOption.optionValueDecimal) {
                        value = surveyQuestionOption.optionValueDecimal;
                        type = "decimal";
                    }
                    if (surveyQuestionOption.optionValueFloat) {
                        value = surveyQuestionOption.optionValueFloat;
                    }
                    if (surveyQuestionOption.optionValueDate) {
                        value = surveyQuestionOption.optionValueDate;
                        type = "float";
                    }
                    if (surveyQuestionOption.optionValueBoolean) {
                        value = surveyQuestionOption.optionValueBoolean;
                        type = "boolean";
                    }
                    return {
                        key: key,
                        text: key,
                        value: value,
                        iconUrl: iconUrl,
                        iconSvg: iconSvg,
                        type: type,
                        surveyQuestionOptionId: surveyQuestionOption.surveyQuestionOptionId

                    };
                });
            }

            let minDate = new Date(1900, 1, 1)
            let maxDate = new Date(2099, 1, 1)
            let showDate = questionType.hasDate;
            let showTime = questionType.hasTime;
            let typeName = surveyQuestion.surveyQuestionType.typeName;

            return {
                surveyQuestionId: surveyQuestionId,
                surveyQuestionOptions: surveyQuestionOptions,
                label: label,
                labelAdditional: labelAdditional,
                name: name,
                nameAdditional: nameAdditional,
                rules: rules,
                rulesAdditional: rulesAdditional,
                readOnly: readOnly,
                tabIndex: tabIndex,
                initialValue: initialValue,
                instructions: instructions,
                errorMessage: errorMessage,
                errorMessageAdditional: errorMessageAdditional,
                errorMessages: errorMessages,
                minDate: minDate,
                maxDate: maxDate,
                showDate: showDate,
                showTime: showTime,
                typeName: typeName,
                questionSequence: questionSequence,
                responseField: responseField,
                responseAdditionalField: responseAdditionalField,
                requiresCorrespondence: requiresCorrespondence

            };
        });



        setSurveyQuestions(newSurveyQuestions);
        setSurveyQuestionsRef(newSurveyQuestions);
    }

    const initializeSurveyQuestionResponses = () => {
        let newSurveyModal = { ...surveyModalRef.current };

        let newSurveyResponses = surveyQuestionsRef.current.map(question => {
            return {
                surveyQuestionResponseId: 0,
                surveyResponseId: 0,
                surveyQuestionId: question.surveyQuestionId,
                surveyQuestionResponseStatusId: 2,
                intValue: null,
                longValue: null,
                decimalValue: null,
                floatValue: null,
                stringValue: null,
                dateValue: null,
                booleanValue: null,
                additionalStringValue: null,
                surveyQuestionResponseSurveyQuestionOptionTags: [],
                answeredOn: null,
                isValid: false,
            };
        });
        newSurveyModal.surveyQuestionResponses = newSurveyResponses;


        setSurveyModalRef(newSurveyModal);
    }

    const setValuesForSurveyQuestionsWithoutOptions = (question, surveyQuestion, surveyQuestionResponse, isAdditional) => {
        let fieldName = lowerCaseFirstLetter(surveyQuestion.responseField);
        if (isAdditional) {
            fieldName = lowerCaseFirstLetter(surveyQuestion.responseAdditionalField);
        }
        surveyQuestionResponse[fieldName] = question.value;


        if (question.value != null && question.value !== '') {
            surveyQuestionResponse.answeredOn = new Date();
            surveyQuestionResponse.surveyQuestionResponseStatusId = 1;
        }

    }

    const setValuesForSurveyQuestionsWithOptions = (question, surveyQuestion, surveyQuestionResponse, isAdditional) => {

        if (question.value != null) {
            surveyQuestionResponse.answeredOn = new Date();
            surveyQuestionResponse.surveyQuestionResponseStatusId = 1;

            if (Array.isArray(question.value)) {
                var options = [];

                question.value.forEach(option => {
                    let surveyQuestionOption = getSurveyQuestionOptionFromResponse(option, surveyQuestion);
                    if (surveyQuestionOption) {
                        options.push({ surveyQuestionResponseId: 0, surveyQuestionOptionId: surveyQuestionOption.surveyQuestionOptionId });
                    }
                });

                surveyQuestionResponse.surveyQuestionResponseSurveyQuestionOptionTags = options;

            } else if (isObject(question.value)) {
                let surveyQuestionOption = getSurveyQuestionOptionFromResponse(question.value, surveyQuestion);

                if (surveyQuestionOption) {
                    surveyQuestionResponse.surveyQuestionResponseSurveyQuestionOptionTags = [{ surveyQuestionResponseId: 0, surveyQuestionOptionId: surveyQuestionOption.surveyQuestionOptionId }];
                    setSurveyQuestionResponseValueFromResponse(question.value.value, surveyQuestionResponse, surveyQuestionOption);
                }

            } else {
                let fieldName = lowerCaseFirstLetter(surveyQuestion.responseField);

                if (isAdditional) {
                    fieldName = lowerCaseFirstLetter(surveyQuestion.responseAdditionalField);
                }

                surveyQuestionResponse[fieldName] = question.value;
            }
        }
    }


  const validateQuestion = (question) => {
    if (logComponent('SurveyModal')) { console.debug('SurveyModal validateField'); }
    let isAdditional = false;

    let newSurveyModal = { ...surveyModalRef.current };
    let surveyQuestion = surveyQuestionsRef.current.filter((x) => x.name === question.name)[0];
    if (surveyQuestion == null) {
      surveyQuestion = surveyQuestionsRef.current.filter((x) => x.nameAdditional === question.name)[0];
      if (surveyQuestion != null) {
        isAdditional = true;
      }

    }
    let surveyQuestionResponse = newSurveyModal.surveyQuestionResponses.find(({ surveyQuestionId }) => surveyQuestionId === surveyQuestion.surveyQuestionId);
    let surveyQuestionResponseIndex = newSurveyModal.surveyQuestionResponses.findIndex(({ surveyQuestionId }) => surveyQuestionId === surveyQuestion.surveyQuestionId);
    surveyQuestionResponse.isValid = question.isValid;
    if (surveyQuestion.responseField === 'checkOptions') {
      setValuesForSurveyQuestionsWithOptions(question, surveyQuestion, surveyQuestionResponse, isAdditional);

    } else {
      setValuesForSurveyQuestionsWithoutOptions(question, surveyQuestion, surveyQuestionResponse, isAdditional);
    }
    newSurveyModal.surveyQuestionResponses[surveyQuestionResponseIndex] = surveyQuestionResponse;
    setSurveyModalRef(newSurveyModal);
  }

  const getSurveyQuestionOptionFromResponse = (response, surveyQuestion) => {

    let returnOption = surveyQuestion.surveyQuestionOptions.filter((x) => x.text === response.text)[0];

    return returnOption;

  }

  const setSurveyQuestionResponseValueFromResponse = (response, surveyQuestionResponse, surveyQuestionOption) => {
    if (surveyQuestionOption.type === 'string') {
      surveyQuestionResponse.stringValue = response;
    }
    if (surveyQuestionOption.type === 'int') {
      surveyQuestionResponse.intValue = response;
    }
    if (surveyQuestionOption.type === 'long') {
      surveyQuestionResponse.longValue = response;
    }
    if (surveyQuestionOption.type === 'decimal') {
      surveyQuestionResponse.decimalValue = response;
    }
    if (surveyQuestionOption.type === 'float') {
      surveyQuestionResponse.floatValue = response;
    }
    if (surveyQuestionOption.type === 'boolean') {
      surveyQuestionResponse.booleanValue = response;
    }
  }

  const submitSurvey = () => {
    if (logComponent('SurveyModal')) { console.debug('SurveyModal submitSurvey'); }

    let surveyResponseToSubmit = { ...surveyModalRef.current };

    let noAccessQuestionIds = canCorrespond ? [] : surveyQuestions.filter((item) => item.requiresCorrespondence === true).map((item) => item.surveyQuestionId);
    let invalidQuestions = surveyResponseToSubmit.surveyQuestionResponses.filter((item) => item.isValid === false && !noAccessQuestionIds.includes(item.surveyQuestionId));
    let isValid = invalidQuestions === null || invalidQuestions.length == 0;


    if (!isValid) {
      handleInvalidSubmission();
      return;
    }

    setFormIsInvalidRef(false);




    // Add end time, progress 
    surveyResponseToSubmit.endOn = new Date();
    surveyResponseToSubmit.localEndOn = new Date();
    let answeredQuestions = surveyResponseToSubmit.surveyQuestionResponses.filter(x => x.surveyQuestionResponseStatusId === 1 || x.surveyQuestionResponseStatusId === 2);
    let allQuestionsLength = surveyResponseToSubmit.surveyQuestionResponses.length;
    let answeredQuestionsLength = answeredQuestions.length;
    let progress = answeredQuestionsLength / allQuestionsLength;
    surveyResponseToSubmit.surveyProgress = progress;
    surveyResponseToSubmit.isComplete = (progress === 1);
    

    setStatus('pending');


    baseApi.Survey.SaveResponse(surveyResponseToSubmit)
      .then(
        result => {
          setStatus('success');
          startModalExit();
        },
        error => {
          setStatus('failure');
        }
      )
  }

  const cancelSurvey = () => {
    if (logComponent('SurveyModal')) { console.debug('SurveyModal cancelSurvey'); }
    startModalExit();
  }

  const handleInvalidSubmission = () => {
    if (logComponent('SurveyModal')) { console.debug('SurveyModal handleInvalidSubmission'); }
    modalRef.current.scrollTop = 0;
    setFormIsInvalidRef(true);

  }


  const startModalExit = () => {
    if (logComponent('SurveyModal')) { console.debug('SurveyModal startModalExit'); }
    setModalShouldExit(true);
  }
  const isObject = (val) => {
    if (logComponent('SurveyModal')) { console.debug('SurveyModal isObject'); }
    return val instanceof Object;
  }

  const handleShiftTab = (e) => {
    if (logComponent('SurveyModal')) { console.debug('SurveyModal handleShiftTab'); }
    const { key, shiftKey } = e;

    if (key !== 'Tab' || !shiftKey) return;

    e.preventDefault();
  }

  return (
    <Modal isVisible={true} shouldExit={modalShouldExit} hide={close} ref={setModalRef} remainOnBackdropClick={true}>
      <div className="Survey-Modal">
        <h4 className="Survey-Modal__heading">
          <span>{title}</span>
          <Timer startTime={startTime} />
        </h4>
        <p className={'Survey-Modal__page-error' + (formIsInvalid ? ' visible' : '')}>There were some errors. Please double check the information and resubmit.</p>
        <p className={"Survey-Modal__page-error" + (status === 'failure' ? ' visible' : '')} >Sorry, something went wrong.  Changes were not saved.  Please try again.</p>

        <div className="Survey-Modal__monitored-individual-info">
          <div className="Survey-Modal__monitored-individual-image">
            <MonitoredIndividualImage monitoredIndividual={selectedMonitoredIndividual } />
            
          </div>
          <div className="Survey-Modal__monitored-individual-text">
            {
              selectedMonitoredIndividual &&
              <p className="Survey-Modal__monitored-individual-name">{`${selectedMonitoredIndividual.legalFirstName} ${selectedMonitoredIndividual.lastName}`}</p>
            }
            <p className="Survey-Modal__monitored-individual-units">
              {
                selectedMonitoredIndividual ?
                  selectedMonitoredIndividual.monitoredIndividualUnits.map((individualUnit, index) =>
                    <span key={individualUnit.unit.unitId}>{(index > 0 ? ', ' : '') + individualUnit.unit.name}</span>
                  ) :
                  selectedUnit ?
                    <span>{selectedUnit.name}</span> :
                    null
              }
            </p>
            <p className="Survey-Modal__facility-info">
              {organization.name}
            </p>
          </div>
        </div>
        {surveyQuestions &&
          <form className="Survey-Modal__form">
            <div>
              {
                surveyQuestions
                  .filter(question => question.requiresCorrespondence === false || canCorrespond === true)
                  .map(surveyQuestion =>

                  <div key={surveyQuestion.surveyQuestionId}>
                    {
                      surveyQuestion.typeName === 'Undefined/No Type' &&
                      <TextAreaInput
                        label={surveyQuestion.label}
                        name={surveyQuestion.name}
                        rules={surveyQuestion.rules}
                        errorMessage={surveyQuestion.errorMessage}
                        showError={formIsInvalidRef.current}
                        onValidate={validateQuestion}

                      />
                    }
                    {
                      surveyQuestion.typeName === 'Multiple Choice' &&
                      <RadioButtonListInput
                        initialValue={surveyQuestion.initialValue}
                        label={surveyQuestion.label}
                        name={surveyQuestion.name}
                        showError={formIsInvalidRef.current}
                        errorMessage={surveyQuestion.errorMessage}
                        rules={surveyQuestion.rules}
                        onValidate={validateQuestion}
                        items={surveyQuestion.surveyQuestionOptions}
                      />

                    }
                    {
                      surveyQuestion.typeName === 'Numeric (integer)' &&
                      <IntegerInput
                        initialValue={surveyQuestion.initialValue}
                        rules={surveyQuestion.rules}
                        onValidate={validateQuestion}
                        name={surveyQuestion.name}
                        label={surveyQuestion.label}
                        instructions={surveyQuestion.instructions}
                        showError={formIsInvalidRef.current}
                        errorMessages={surveyQuestion.errorMessages}
                        readOnly={surveyQuestion.readOnly}
                        tabIndex={surveyQuestion.tabIndex}
                      />
                    }
                    {
                      surveyQuestion.typeName === 'Numeric (decimal)' &&
                      <DecimalInput
                        initialValue={surveyQuestion.initialValue}
                        rules={surveyQuestion.rules}
                        onValidate={validateQuestion}
                        name={surveyQuestion.name}
                        label={surveyQuestion.label}
                        instructions={surveyQuestion.instructions}
                        showError={formIsInvalidRef.current}
                        errorMessages={surveyQuestion.errorMessages}
                        readOnly={surveyQuestion.readOnly}
                        tabIndex={surveyQuestion.tabIndex}
                      />
                    }
                    {
                      surveyQuestion.typeName === 'Time' &&
                      <DateTimeInput
                        initialValue={surveyQuestion.initialValue}
                        rules={surveyQuestion.rules}
                        minDate={surveyQuestion.minDate}
                        maxDate={surveyQuestion.maxDate}
                        onValidate={validateQuestion}
                        name={surveyQuestion.name}
                        label={surveyQuestion.label}
                        instructions={surveyQuestion.instructions}
                        showError={formIsInvalidRef.current}
                        errorMessages={surveyQuestion.errorMessages}
                        readOnly={surveyQuestion.readOnly}
                        tabIndex={surveyQuestion.tabIndex}
                        showDate={surveyQuestion.showDate}
                        showTime={surveyQuestion.showTime}
                      />
                    }
                    {
                      surveyQuestion.typeName === 'Time/Date' &&
                      <DateTimeInput
                        initialValue={surveyQuestion.initialValue}
                        rules={surveyQuestion.rules}
                        minDate={surveyQuestion.minDate}
                        maxDate={surveyQuestion.maxDate}
                        onValidate={validateQuestion}
                        name={surveyQuestion.name}
                        label={surveyQuestion.label}
                        instructions={surveyQuestion.instructions}
                        showError={formIsInvalidRef.current}
                        errorMessages={surveyQuestion.errorMessages}
                        readOnly={surveyQuestion.readOnly}
                        tabIndex={surveyQuestion.tabIndex}
                        showDate={surveyQuestion.showDate}
                        showTime={surveyQuestion.showTime}
                      />
                    }
                    {
                      surveyQuestion.typeName === 'Date' &&
                      <DateTimeInput
                        initialValue={surveyQuestion.initialValue}
                        rules={surveyQuestion.rules}
                        minDate={surveyQuestion.minDate}
                        maxDate={surveyQuestion.maxDate}
                        onValidate={validateQuestion}
                        name={surveyQuestion.name}
                        label={surveyQuestion.label}
                        instructions={surveyQuestion.instructions}
                        showError={formIsInvalidRef.current}
                        errorMessages={surveyQuestion.errorMessages}
                        readOnly={surveyQuestion.readOnly}
                        tabIndex={surveyQuestion.tabIndex}
                        showDate={surveyQuestion.showDate}
                        showTime={surveyQuestion.showTime}
                      />
                    }
                    {
                      surveyQuestion.typeName === 'Large Text Entry' &&
                      <TextAreaInput
                        name={surveyQuestion.name}
                        label={surveyQuestion.label}
                        rules={surveyQuestion.rules}
                        errorMessage={surveyQuestion.errorMessage}
                        showError={formIsInvalidRef.current}
                        onValidate={validateQuestion}

                      />
                    }
                    {
                      surveyQuestion.typeName === 'Small Text Entry' &&
                      <TextInput
                        initialValue={surveyQuestion.initialValue}
                        label={surveyQuestion.label}
                        name={surveyQuestion.name}
                        rules={surveyQuestion.rules}
                        errorMessages={surveyQuestion.errorMessages}
                        showError={formIsInvalidRef.current}
                        validate={validating}
                        onValidate={validateQuestion}
                        onKeyDown={handleShiftTab} />

                    }
                    {
                      surveyQuestion.typeName === 'Toggle' &&
                      <ToggleInput
                        initialValue={surveyQuestion.initialValue}
                        label={surveyQuestion.label}
                        name={surveyQuestion.name}
                        showError={formIsInvalidRef.current}
                        errorMessage={surveyQuestion.errorMessageAdditional}
                        rules={surveyQuestion.rules}
                        onValidate={validateQuestion}

                      />

                    }
                    {
                      surveyQuestion.typeName === 'Slider' &&
                      <NumericSlider
                        initialValue={surveyQuestion.surveyQuestionOptions[0].value}
                        showError={formIsInvalidRef.current}
                        errorMessages={surveyQuestion.errorMessages}
                        rules={surveyQuestion.rules}
                        onValidate={validateQuestion}
                        name={surveyQuestion.name}
                        minRange={surveyQuestion.surveyQuestionOptions[0].value}
                        maxRange={surveyQuestion.surveyQuestionOptions[surveyQuestion.surveyQuestionOptions.length-1].value}
                        items={surveyQuestion.surveyQuestionOptions}
                      />
                    }
                    {
                      surveyQuestion.typeName === 'Graphic Slider' &&

                      <GraphicalSlider
                        initialValue={surveyQuestion.surveyQuestionOptions[0].value}
                        showError={formIsInvalidRef.current}
                        errorMessages={surveyQuestion.errorMessages}
                        rules={surveyQuestion.rules}
                        onValidate={validateQuestion}
                        name={surveyQuestion.name}
                        minRange={surveyQuestion.surveyQuestionOptions[0].value}
                        maxRange={surveyQuestion.surveyQuestionOptions[surveyQuestion.surveyQuestionOptions.length - 1].value}
                        items={surveyQuestion.surveyQuestionOptions}
                        label={surveyQuestion.label}
                      />
                    }
                    {
                      surveyQuestion.typeName === 'Multiple Choice List with Search' &&

                      <SearchSelect
                        label={surveyQuestion.label}
                        selectionMode="limit"
                        itemKeyField="value"
                        items={surveyQuestion.surveyQuestionOptions}
                        itemsLoading={false}
                        itemsError={false}
                        itemPluralTerm="items"
                        searchFieldGroups={[
                          ['key']
                        ]}
                        displayField="key"
                        name={surveyQuestion.name}
                        rules={surveyQuestion.rules}
                        showError={formIsInvalidRef.current}
                        errorMessage={surveyQuestion.errorMessage}
                        onValidate={validateQuestion}
                      />
                    }
                    {
                      surveyQuestion.typeName === 'Multiple Choice with Text Entry' &&

                      <div>
                        <SearchSelect
                          label={surveyQuestion.label}
                          selectionMode="limit"
                          itemKeyField="value"
                          items={surveyQuestion.surveyQuestionOptions}
                          itemsLoading={false}
                          itemsError={false}
                          itemPluralTerm="items"
                          searchFieldGroups={[
                            ['key']
                          ]}
                          displayField="key"
                          name={surveyQuestion.name}
                          rules={surveyQuestion.rules}
                          showError={formIsInvalidRef.current}
                          errorMessage={surveyQuestion.errorMessage}
                          onValidate={validateQuestion}
                        />
                        <TextAreaInput
                          label={surveyQuestion.labelAdditional}
                          name={surveyQuestion.nameAdditional}
                          rules={surveyQuestion.rulesAdditional}
                          errorMessage={surveyQuestion.errorMessageAdditional}
                          showError={formIsInvalidRef.current}
                          onValidate={validateQuestion}

                        />
                      </div>
                    }

                  </div>
                )
              }
              <ActionButtons
                ref={actionButtons}
                mainAction={submitSurvey}
                mainActionText="Submit survey"
                cancel={cancelSurvey}
                cancelText="Cancel"
                currentStatus={status} />
            </div>
          </form>
        }
      </div>
    </Modal>

  );
}
